import { action, makeObservable, observable, reaction, runInAction } from 'mobx';
import { keymap } from 'prosemirror-keymap';
import {
  EditorState as PMState,
  type EditorState as PMEditorState,
} from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import {
  findNext as searchFindNext,
  findPrev as searchFindPrev,
  getMatchHighlights,
  replaceAll as searchReplaceAll,
  replaceNext as searchReplaceNext,
  search,
  SearchQuery,
  setSearchState,
} from 'prosemirror-search';
import {
  acceptAllSuggestions,
  rejectAllSuggestions,
  setSuggestionMode,
} from 'prosemirror-suggestion-mode';

import { ExportDisposition } from '@clipr/lib';

import { notifyError } from '../../services/notifications';
import { StoreNode } from '../../store';
import { type Store } from '../../store/store';

import { schema } from './schema';
import { clipboardSerializer, clipboardText, docToHTML, htmlToDoc } from './schemaSerializer';
import { historyPlugin } from './plugins/history';
import { keymapPlugin, baseKeymapPlugin } from './plugins/keymap';
import { inputRulesPlugin } from './plugins/inputRules';
import { imageUploadPlugin, startImageUpload } from './plugins/imageUpload';
import { imageResizePlugin } from './plugins/imageResize';
import { trackChangesPlugin } from './plugins/trackChanges';
import {
  applyTargetHtmlAsSuggestions,
} from './suggestions/applyTargetHtmlAsSuggestions';
import type { BlockDiff } from './suggestions/blockDiffTypes';
import {
  getPersistedTextDirection,
  type TextDirection,
} from './helpers/getPersistedTextDirection';
import { printPdfInFrame } from './printPdf';
import type { ReportBranding } from './reportBranding';
import { GrammarChecker } from './grammar/grammarChecker';
import { grammarIssueAt, grammarPlugin } from './grammar/grammarPlugin';
import type { BlockLintFn, GrammarIssue } from './grammar/types';
export type { TextDirection };

export type SaveStatus = 'idle' | 'typing' | 'saving' | 'saved' | 'error';

export type SaveHandler = (content: string) => Promise<boolean | void> | boolean | void;

const GRAMMAR_PREF_KEY = 'clipr-editor-grammar';

function readGrammarPreference(): boolean {
  try {
    return window.localStorage.getItem(GRAMMAR_PREF_KEY) !== 'off';
  } catch {
    return true;
  }
}

export class TextEditorState extends StoreNode {
  readonly nodeType = 'TextEditor';

  constructor(store: Store) {
    super(store);
    makeObservable(this, {
      editorModel: observable,
      jobId: observable,
      branding: observable,
      isMounted: observable,
      reportDiff: observable,
      isDiffLoading: observable,
      originalContent: observable,
      isExportingPdf: observable,
      isSaving: observable,
      isForcedSaving: observable,
      viewTick: observable,
      textDirection: observable,
      isSourceView: observable,
      isSuggestionsOn: observable,
      readOnly: observable,
      saveStatus: observable,
      lastSavedAt: observable,
      isFindOpen: observable,
      findQuery: observable,
      findReplacement: observable,
      findCaseSensitive: observable,
      isGrammarOn: observable,
      grammarIssueCount: observable,
      activeGrammarIssue: observable,
      mounted: action,
      unmounted: action,
      setReadOnly: action,
      setJobId: action,
      setBranding: action,
      markDirty: action,
      openFind: action,
      closeFind: action,
      setFindQuery: action,
      setFindReplacement: action,
      toggleFindCaseSensitive: action,
      toggleGrammar: action,
      applyGrammarSuggestion: action,
      ignoreGrammarIssue: action,
      closeGrammarPopup: action,
      setSourceView: action,
      setEditorModelFromSource: action,
      setReportDiff: action,
      setDiffLoadingState: action,
      setTextDirection: action,
      toggleSuggestionMode: action,
      acceptAllSuggestions: action,
      rejectAllSuggestions: action,
      applyBackendDiff: action,
      resetToOriginalContent: action,
    });
  }

  view: EditorView | null = null;

  editorModel: string | null = null;
  jobId: string | null = null;
  /** The customer's page furniture, so the draft on screen matches the export. */
  branding: ReportBranding | null = null;
  /** Bumped per branding load, so a slow response for a previous job cannot land on this one. */
  private brandingGeneration = 0;
  isMounted = false;
  reportDiff: BlockDiff[] | null = null;
  isDiffLoading = false;
  originalContent: string | null = null;
  isExportingPdf = false;
  isSaving = false;
  isForcedSaving = false;
  viewTick = 0;
  textDirection: TextDirection | null = null;
  isSourceView = false;
  isSuggestionsOn = false;
  readOnly = false;
  saveStatus: SaveStatus = 'idle';
  lastSavedAt: number | null = null;

  isFindOpen = false;
  findQuery = '';
  findReplacement = '';
  findCaseSensitive = false;

  isGrammarOn = readGrammarPreference();
  grammarIssueCount = 0;
  activeGrammarIssue: GrammarIssue | null = null;

  // Overridable in tests so the debounce does not use a real 2s timer.
  autoSaveDelayMs = 2000;

  // Injectable so tests don't need the harper.js WASM binary. Returns null
  // where Web Workers don't exist (jsdom) — grammar is silently unavailable.
  grammarLintProvider: () => Promise<BlockLintFn | null> = async () => {
    if (typeof Worker === 'undefined') return null;
    const [{ harperLintFn }, { composeLintFns, customLintFn }] = await Promise.all([
      import('./grammar/harperLinter'),
      import('./grammar/customLintFn'),
    ]);
    return composeLintFns(harperLintFn, customLintFn);
  };

  private reportDiffDisposer: (() => void) | null = null;
  private diffLoadingDisposer: (() => void) | null = null;
  private saveHandler: SaveHandler | null = null;
  private activeSave: Promise<boolean> | null = null;
  private autoSaveTimer: ReturnType<typeof setTimeout> | null = null;
  private suppressAutoSave = false;
  private grammarChecker: GrammarChecker | null = null;

  mounted(initialModel: string | null) {
    this.editorModel = initialModel;
    this.textDirection = getPersistedTextDirection(initialModel);
    this.isMounted = true;

    this.reportDiffDisposer = reaction(
      () => this.reportDiff,
      (diff) => {
        if (diff && diff.length > 0 && this.view) {
          try {
            this.applyBackendDiff(diff);
          } catch (e) {
            console.error('[AI-SUMMARY] applyBackendDiff error:', e);
          }
        }
      },
    );

    this.diffLoadingDisposer = reaction(
      () => [this.isDiffLoading, this.originalContent, this.reportDiff] as const,
      ([loading, original, currentDiff]) => {
        if (loading && original !== null && !currentDiff) {
          this.resetToOriginalContent(original);
        }
      },
    );
  }

  unmounted() {
    this.cancelAutoSave();
    this.saveHandler = null;
    this.activeSave = null;
    this.isSaving = false;
    this.isForcedSaving = false;
    this.saveStatus = 'idle';
    this.lastSavedAt = null;
    this.grammarChecker?.destroy();
    this.grammarChecker = null;
    this.grammarIssueCount = 0;
    this.activeGrammarIssue = null;
    this.isFindOpen = false;
    this.findQuery = '';
    this.findReplacement = '';
    this.findCaseSensitive = false;
    this.reportDiffDisposer?.();
    this.reportDiffDisposer = null;
    this.diffLoadingDisposer?.();
    this.diffLoadingDisposer = null;
    this.view?.destroy();
    this.view = null;
    this.editorModel = null;
    this.textDirection = null;
    this.branding = null;
    this.isMounted = false;
  }

  setSaveHandler(handler: SaveHandler | null) {
    this.saveHandler = handler;
    if (!handler) this.cancelAutoSave();
  }

  setReadOnly(readOnly: boolean) {
    this.readOnly = readOnly;
    this.view?.setProps({
      editable: () => !this.readOnly,
      clipboardSerializer,
      clipboardTextSerializer: clipboardText,
    });
  }

  setJobId(jobId: string | null) {
    this.jobId = jobId;
  }

  setBranding(branding: ReportBranding | null) {
    this.branding = branding;
  }

  /**
   * Loads the branding recorded against the job's custom report format.
   *
   * Silent on every failure. A member who cannot read the team, a job with no custom format, a
   * format trained before branding was captured -- all mean the same thing to the editor: show the
   * plain surface. None of them is worth an error in front of someone trying to edit a report.
   */
  loadBranding = async (jobId?: string | null) => {
    // Every load is numbered, and only the newest may write. The window is reused across jobs and
    // the callers do not await it, so opening one report and quickly opening another leaves two
    // requests in flight -- and the slower one would otherwise paint the first customer's
    // letterhead, footer and typeface onto the second customer's report.
    const generation = this.brandingGeneration + 1;
    this.brandingGeneration = generation;

    if (!jobId) return;

    // Actually silent, not merely documented as such. Callers fire this with `void`, so anything
    // thrown here becomes an unhandled rejection rather than a plain surface. It used to look
    // silent only because the old specialty-id guard returned before reaching the request.
    try {
      // Asked of the job, not of a specialty: a job with no report format of its own still belongs
      // to an agency and still carries their letterhead. The server decides which applies, so the
      // preview and the export cannot disagree about it.
      const [res] = await this.store.api.getJobReportBranding({ jobId });
      if (generation !== this.brandingGeneration) return;

      const branding = res?.getJobReportBranding;
      if (!branding) return;

      this.setBranding({
        fontFamily: branding.fontFamily ?? undefined,
        fontSizePt: branding.fontSizePt ?? undefined,
        marginInches: branding.pageMarginInches ?? undefined,
        pageSizeInches: branding.pageSizeInches ?? undefined,
        letterheadUrl: branding.logoUrl ?? undefined,
        letterheadInches: branding.logoInches ?? undefined,
        footerHtml: branding.footerHtml ?? undefined,
      });
    } catch {
      // Branding is decoration. Losing it must never cost someone the report they are editing.
    }
  };

  async uploadImage(file: File): Promise<string> {
    if (!this.jobId) throw new Error('Cannot upload image without a jobId');
    const [res, err] = await this.store.apiService.getReportImageUploadUrl({
      jobId: this.jobId,
      fileName: file.name,
    });
    if (err || !res) throw new Error('Could not get an upload URL');
    const params = res.getReportImageUploadUrl;
    const [, uploadErr] = await this.store.apiService
      .uploadRequest({ file, url: params.uploadUrl, data: params.fields })
      .start();
    if (uploadErr) throw new Error('Upload failed');
    return params.signedUrl;
  }

  private handleImageFile(view: EditorView, file: File): void {
    // prosemirror-suggestion-mode tracks text steps; it does not reliably wrap an
    // inline atom (image) as an insert suggestion, and the AI-diff offset map walks
    // text nodes only — so a pasted image while reviewing suggestions can be lost or
    // mis-positioned. Block it with a clear notice instead.
    if (this.isSuggestionsOn) {
      notifyError(this, 'Turn off suggestions mode to add an image.');
      return;
    }
    startImageUpload(view, file, (f) => this.uploadImage(f).catch((e: unknown) => {
      notifyError(this, 'Could not upload the image. Please try again.');
      throw e;
    }));
  }

  // Toolbar entry point: open a native file picker and route the chosen image
  // through the same upload path as paste/drop (placeholder + suggestions guard).
  pickAndUploadImage = () => {
    if (!this.view) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files?.[0];
      if (file && this.view) this.handleImageFile(this.view, file);
    };
    input.click();
  };

  markDirty() {
    if (!this.saveHandler || this.readOnly) return;
    // While AI suggestions are displayed the doc holds suggestion marks that
    // get stripped on save — auto-saving here would persist content identical
    // to the original and tear down the diff review state mid-session.
    if (this.reportDiff) return;
    this.saveStatus = 'typing';
    this.scheduleAutoSave();
  }

  saveNow = async (): Promise<boolean> => {
    if (!this.saveHandler) return false;
    if (this.hasUnresolvedSuggestions) return false;
    if (this.activeSave) return this.activeSave;
    this.cancelAutoSave();
    return this.startSave(true);
  };

  private startSave(forced: boolean): Promise<boolean> {
    runInAction(() => {
      this.isSaving = true;
      this.isForcedSaving = forced;
      this.saveStatus = 'saving';
    });
    const save = this.runSave().finally(() => {
      runInAction(() => {
        if (this.activeSave !== save) return;
        this.activeSave = null;
        this.isSaving = false;
        this.isForcedSaving = false;
      });
    });
    this.activeSave = save;
    return save;
  }

  private scheduleAutoSave() {
    this.cancelAutoSave();
    this.autoSaveTimer = setTimeout(() => {
      this.autoSaveTimer = null;
      void this.performAutoSave();
    }, this.autoSaveDelayMs);
  }

  private cancelAutoSave() {
    if (this.autoSaveTimer !== null) {
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  }

  private performAutoSave = async () => {
    if (!this.saveHandler || this.reportDiff || this.hasUnresolvedSuggestions) return;
    // Manual save in flight — let it finish, then retry.
    if (this.activeSave) {
      this.scheduleAutoSave();
      return;
    }
    await this.startSave(false);
  };

  private runSave = async (): Promise<boolean> => {
    if (!this.saveHandler) return false;
    try {
      const content = this.getCleanHTML();
      const ok = await this.saveHandler(content);
      const contentStayedCurrent = this.saveStatus === 'saving' && this.getCleanHTML() === content;
      runInAction(() => {
        // User typed again while the request was in flight — a new auto-save
        // is already scheduled, keep showing 'typing'.
        if (!contentStayedCurrent) return;
        if (ok === false) {
          this.saveStatus = 'error';
        } else {
          this.saveStatus = 'saved';
          this.lastSavedAt = Date.now();
        }
      });
      return ok !== false && contentStayedCurrent;
    } catch {
      runInAction(() => {
        if (this.saveStatus === 'saving') this.saveStatus = 'error';
      });
      return false;
    }
  };

  attachView(parent: HTMLElement, userId: string) {
    if (this.view) return;
    // A source-view round trip destroys the previous view (textEditor.tsx)
    // but not this state — drop anything still bound to the dead view or
    // its document positions, so startGrammar below builds a fresh checker.
    this.grammarChecker?.destroy();
    this.grammarChecker = null;
    runInAction(() => { this.activeGrammarIssue = null; });
    const initialState = PMState.create({
      doc: htmlToDoc(this.editorModel),
      schema,
      plugins: [
        historyPlugin,
        this.editorActionsKeymapPlugin(),
        keymapPlugin,
        baseKeymapPlugin,
        inputRulesPlugin,
        imageUploadPlugin,
        // After imageUploadPlugin, so a placeholder has become a real image by the time this
        // draws a handle on it.
        imageResizePlugin(),
        search(),
        grammarPlugin(),
        trackChangesPlugin({ userId, enabled: this.isSuggestionsOn }),
      ],
    });
    this.view = new EditorView(parent, {
      state: initialState,
      attributes: { spellcheck: 'false' },
      editable: () => !this.readOnly,
      handleClick: (view, pos) => {
        const issue = this.isGrammarOn ? grammarIssueAt(view.state, pos) : null;
        runInAction(() => { this.activeGrammarIssue = issue; });
        return false;
      },
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items ?? []);
        const imageItem = items.find((i) => i.kind === 'file' && i.type.startsWith('image/'));
        if (!imageItem) return false;
        const file = imageItem.getAsFile();
        if (!file) return false;
        this.handleImageFile(view, file);
        return true;
      },
      handleDrop: (view, event) => {
        const dt = (event as DragEvent).dataTransfer;
        const files = Array.from(dt?.files ?? []).filter((f) => f.type.startsWith('image/'));
        if (files.length === 0) return false;
        files.forEach((f) => this.handleImageFile(view, f));
        return true;
      },
      dispatchTransaction: (tr) => {
        if (!this.view) return;
        // editable:false only stops typing. Find/Replace, grammar fixes, image resize and
        // Accept All dispatch their own edits, so a read-only document refuses them here.
        // Programmatic loads (suggestion overlay, text direction) set suppressAutoSave.
        if (this.readOnly && tr.docChanged && !this.suppressAutoSave) return;
        const next = this.view.state.apply(tr);
        this.view.updateState(next);
        runInAction(() => {
          this.viewTick += 1;
          if (tr.docChanged) {
            this.editorModel = docToHTML(next.doc);
            this.activeGrammarIssue = null;
            this.grammarChecker?.schedule();
            if (!this.suppressAutoSave) {
              this.markDirty();
            }
          }
        });
      },
    });
    // Re-attach after a source-view toggle starts from a fresh plugin state —
    // restore the active search and grammar loop.
    if (this.isFindOpen && this.findQuery) this.applySearchQuery();
    if (this.isGrammarOn) void this.startGrammar();
  }

  private editorActionsKeymapPlugin() {
    return keymap({
      'Mod-f': () => {
        this.openFind();
        return true;
      },
      Escape: () => {
        if (this.activeGrammarIssue) {
          runInAction(() => { this.activeGrammarIssue = null; });
          return true;
        }
        if (this.isFindOpen) {
          this.closeFind();
          return true;
        }
        return false;
      },
    });
  }

  getCurrentState(): PMEditorState | null {
    return this.view?.state ?? null;
  }

  openFind() {
    if (this.view) {
      const { from, to } = this.view.state.selection;
      if (from !== to) {
        const selected = this.view.state.doc.textBetween(from, to, ' ', ' ').trim();
        if (selected && !selected.includes('\n')) this.findQuery = selected;
      }
    }
    this.isFindOpen = true;
    this.applySearchQuery();
  }

  closeFind() {
    this.isFindOpen = false;
    if (this.view) {
      this.view.dispatch(setSearchState(this.view.state.tr, new SearchQuery({ search: '' })));
      this.view.focus();
    }
  }

  setFindQuery(query: string) {
    this.findQuery = query;
    this.applySearchQuery();
  }

  setFindReplacement(replacement: string) {
    this.findReplacement = replacement;
    this.applySearchQuery();
  }

  toggleFindCaseSensitive() {
    this.findCaseSensitive = !this.findCaseSensitive;
    this.applySearchQuery();
  }

  private applySearchQuery() {
    if (!this.view) return;
    const query = new SearchQuery({
      search: this.isFindOpen ? this.findQuery : '',
      replace: this.findReplacement,
      caseSensitive: this.findCaseSensitive,
      literal: true,
    });
    this.view.dispatch(setSearchState(this.view.state.tr, query));
  }

  findNext() {
    if (!this.view) return;
    searchFindNext(this.view.state, this.view.dispatch, this.view);
  }

  findPrev() {
    if (!this.view) return;
    searchFindPrev(this.view.state, this.view.dispatch, this.view);
  }

  replaceNext() {
    if (!this.view) return;
    searchReplaceNext(this.view.state, this.view.dispatch, this.view);
  }

  replaceAll() {
    if (!this.view) return;
    searchReplaceAll(this.view.state, this.view.dispatch, this.view);
  }

  /** Current match position ("2 of 14") derived from the search highlights. */
  getFindMatchInfo(): { total: number; current: number } {
    if (!this.view || !this.findQuery) return { total: 0, current: 0 };
    const matches = getMatchHighlights(this.view.state).find();
    const { from, to } = this.view.state.selection;
    const index = matches.findIndex((m) => m.from === from && m.to === to);
    return { total: matches.length, current: index + 1 };
  }

  async startGrammar() {
    if (this.grammarChecker || !this.view) return;
    try {
      const lintFn = await this.grammarLintProvider();
      if (!lintFn || !this.view || this.grammarChecker) return;
      this.grammarChecker = new GrammarChecker({
        view: this.view,
        lintFn,
        isPaused: () => !this.isGrammarOn || !!this.reportDiff,
        onIssues: (issues) => runInAction(() => { this.grammarIssueCount = issues.length; }),
      });
      await this.grammarChecker.run();
    } catch (error: unknown) {
      console.error('[grammar] failed to start checker:', error);
    }
  }

  toggleGrammar() {
    this.isGrammarOn = !this.isGrammarOn;
    try {
      window.localStorage.setItem(GRAMMAR_PREF_KEY, this.isGrammarOn ? 'on' : 'off');
    } catch {
      /* storage unavailable — preference just won't persist */
    }
    this.activeGrammarIssue = null;
    if (this.isGrammarOn) {
      if (this.grammarChecker) void this.grammarChecker.run();
      else void this.startGrammar();
    } else {
      this.grammarIssueCount = 0;
      this.grammarChecker?.clear();
    }
  }

  applyGrammarSuggestion(replacement: string) {
    const active = this.activeGrammarIssue;
    if (!this.view || !active) return;
    this.view.dispatch(this.view.state.tr.insertText(replacement, active.from, active.to));
    this.activeGrammarIssue = null;
    this.view.focus();
  }

  ignoreGrammarIssue() {
    const active = this.activeGrammarIssue;
    if (!active) return;
    this.grammarChecker?.ignore(active);
    this.activeGrammarIssue = null;
    this.view?.focus();
  }

  closeGrammarPopup() {
    this.activeGrammarIssue = null;
  }

  setSourceView(on: boolean) {
    if (on && this.hasUnresolvedSuggestions) {
      notifyError(this, 'Accept or reject track changes before opening source view.');
      return;
    }
    this.isSourceView = on;
  }

  setEditorModelFromSource(html: string) {
    if (this.editorModel === html) return;
    this.editorModel = html;
    if (this.view) {
      const next = PMState.create({
        doc: htmlToDoc(html),
        schema,
        plugins: this.view.state.plugins,
      });
      this.view.updateState(next);
    }
    this.markDirty();
  }

  setReportDiff(diff: BlockDiff[] | null) {
    this.reportDiff = diff;
    // Grammar underlines on top of AI suggestion marks are unreadable —
    // clear them while a diff is displayed, re-lint once it's gone.
    if (diff) {
      this.activeGrammarIssue = null;
      this.grammarChecker?.clear();
    } else if (this.isGrammarOn) {
      this.grammarChecker?.schedule();
    }
  }

  setDiffLoadingState(loading: boolean, originalContent: string | null) {
    this.isDiffLoading = loading;
    this.originalContent = originalContent;
  }

  setTextDirection(direction: TextDirection | null, { overwrite = false } = {}) {
    this.textDirection = direction;
    if (!this.view) return;
    const tr = this.view.state.tr;
    // Returning true ensures PM walks into list items, table cells, blockquote
    // children, etc. — not just top-level paragraphs/headings.
    this.view.state.doc.descendants((node, pos) => {
      // On mount, keep a block's own dir (e.g. the Arabic rows of a bilingual scorecard);
      // only the user's toggle overwrites every block.
      if ((node.type.name === 'paragraph' || node.type.name === 'heading') && (overwrite || !node.attrs.dir)) {
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, dir: direction });
      }
      return true;
    });
    if (tr.steps.length > 0) {
      // Direction changes happen programmatically on mount — they must not
      // count as user edits or they'd trigger an auto-save on open.
      this.suppressAutoSave = true;
      try {
        this.view.dispatch(tr);
      } finally {
        this.suppressAutoSave = false;
      }
    }
  }

  toggleTextDirection() {
    const next: TextDirection = this.textDirection === 'rtl' ? 'ltr' : 'rtl';
    this.setTextDirection(next, { overwrite: true });
  }

  getPersistedTextDirection(): TextDirection | null {
    return this.textDirection;
  }

  toggleSuggestionMode() {
    if (!this.view) return;
    const next = !this.isSuggestionsOn;
    setSuggestionMode(this.view, next);
    this.isSuggestionsOn = next;
  }

  acceptAllSuggestions() {
    if (!this.view) return;
    acceptAllSuggestions(this.view.state, this.view.dispatch);
    this.editorModel = docToHTML(this.view.state.doc);
    if (this.isSuggestionsOn) {
      setSuggestionMode(this.view, false);
      this.isSuggestionsOn = false;
    }
  }

  rejectAllSuggestions() {
    if (!this.view) return;
    rejectAllSuggestions(this.view.state, this.view.dispatch);
    this.editorModel = docToHTML(this.view.state.doc);
    if (this.isSuggestionsOn) {
      setSuggestionMode(this.view, false);
      this.isSuggestionsOn = false;
    }
  }

  applyBackendDiff(blockDiffs: BlockDiff[], username = 'ai') {
    if (!this.view || !this.originalContent) return;
    this.resetToOriginalContent(this.originalContent);
    if (!this.view) return;
    if (!this.isSuggestionsOn) {
      setSuggestionMode(this.view, true);
      this.isSuggestionsOn = true;
    }
    this.suppressAutoSave = true;
    try {
      applyTargetHtmlAsSuggestions(this.view, { blockDiffs, username });
    } finally {
      this.suppressAutoSave = false;
    }
  }

  resetToOriginalContent(content: string) {
    // Content is being replaced programmatically (revision reload, diff
    // reset) — any pending auto-save would persist stale content.
    this.cancelAutoSave();
    if (this.saveStatus === 'typing') this.saveStatus = 'idle';
    if (!this.view) return;
    const next = PMState.create({
      doc: htmlToDoc(content),
      schema,
      plugins: this.view.state.plugins,
    });
    this.view.updateState(next);
    this.editorModel = content;
  }

  getCleanHTML(): string {
    if (!this.view) return this.editorModel ?? '';
    return docToHTML(this.view.state.doc, { stripSuggestions: true });
  }

  /**
   * Renders the current document server-side and hands back a download URL.
   *
   * The single place a PDF of this editor comes from. Printing used to build its own document in
   * an iframe, which is why it printed in the app's own font with none of the team's letterhead,
   * running footer or page geometry -- a second renderer that nothing kept in step with the first.
   */
  private renderPdfUrl = async (
    fileName?: string | null,
    languageCode?: string | null,
    disposition?: ExportDisposition,
  ): Promise<string | undefined> => {
    if (!this.editorModel || this.isExportingPdf) return undefined;

    runInAction(() => { this.isExportingPdf = true; });
    try {
      const [res, err] = await this.store.api.exportTextEditorPdf({
        input: {
          html: this.editorModel,
          languageCode: languageCode ?? undefined,
          fileName: fileName ?? undefined,
          // Lets the backend reapply the team's custom-specialty letterhead and footer.
          jobId: this.jobId ?? undefined,
          disposition,
        },
      });

      if (err || !res) return undefined;
      return res.exportTextEditorPdf.downloadUrl;
    } catch {
      return undefined;
    } finally {
      runInAction(() => { this.isExportingPdf = false; });
    }
  };

  get hasUnresolvedSuggestions(): boolean {
    let found = false;
    this.view?.state.doc.descendants((node) => {
      // Returning false only skips this node's children — descendants still walks
      // its siblings. Assigning the result here would let a later clean node
      // overwrite an earlier hit, so accumulate and stop once one is found.
      if (found) return false;
      if (node.marks.some((mark) => (
        mark.type === schema.marks.suggestion_insert
        || mark.type === schema.marks.suggestion_delete
      ))) {
        found = true;
        return false;
      }
      return true;
    });
    return found;
  }

  exportToPDF = async (
    fileName?: string | null,
    languageCode?: string | null,
    direction?: TextDirection | null,
  ) => {
    const url = await this.renderPdfUrl(fileName, languageCode);
    if (!url) {
      notifyError(this, 'Could not export the PDF. Please try again.');
      return;
    }

    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName || 'document'}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();

    // Direction kept for future server-side hint; ignored client-side now
    void direction;
  };

  /**
   * Prints the same PDF the export produces.
   *
   * The file is fetched and handed to a hidden frame as a blob, which makes it same-origin and so
   * printable directly -- the user gets the print dialog rather than a tab they have to press
   * Cmd+P in. The bytes are the server's, so what prints is exactly what the export produces,
   * letterhead and running footer included.
   *
   * Falling back to a tab if any of that is refused: fetching depends on the bucket's CORS policy,
   * and printing a PDF from a frame is well supported in Chrome and Edge but not everywhere.
   */
  printPDF = async (fileName?: string | null, languageCode?: string | null) => {
    if (this.isExportingPdf) return;

    const url = await this.renderPdfUrl(fileName, languageCode, ExportDisposition.Inline);
    if (!url) {
      notifyError(this, 'Could not prepare the document for printing. Please try again.');
      return;
    }

    try {
      await printPdfInFrame(url);
    } catch {
      // The gesture is long gone by now, so this may be blocked; the export itself still
      // succeeded, and saying so beats appearing to do nothing.
      if (!window.open(url, '_blank')) {
        notifyError(this, 'Your browser blocked the print window. Use Download PDF instead.');
      }
    }
  };
}
